import { useEffect, useMemo, useState } from 'react';
import { fetchAvailableFields } from '@/utils/api';
import { t } from '@/i18n';
import type { AvailableField } from '@/types/dashboard';
import './FieldSuggestionsPanel.css';

interface FieldSuggestionsPanelProps {
  sourceKey: string;
}

function tFallback(key: string, fallback: string): string {
  const text = t(key);
  return text === key ? fallback : text;
}

export function FieldSuggestionsPanel({ sourceKey }: FieldSuggestionsPanelProps) {
  const [fields, setFields] = useState<AvailableField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!sourceKey) {
      setFields([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchAvailableFields(sourceKey)
      .then((data) => {
        setFields(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (controller.signal.aborted) return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [sourceKey]);

  const filteredFields = useMemo(() => {
    if (!searchQuery) return fields;
    const q = searchQuery.toLowerCase();
    return fields.filter(
      (f) => f.label.toLowerCase().includes(q) || f.fieldName.toLowerCase().includes(q),
    );
  }, [fields, searchQuery]);

  if (loading) {
    return <div className="text-muted small">{tFallback('dashboard.fieldsLoading', 'Загрузка полей...')}</div>;
  }

  if (error) {
    return <div className="text-danger small">{error}</div>;
  }

  if (fields.length === 0) {
    const emptyMessage = sourceKey.startsWith('standalone:')
      ? tFallback('dashboard.fieldsEmpty.standalone', 'Этот дашборд не привязан к конкретному объекту (процессу, системе и т. п.). Список полей для подстановки в данном типе дашбордов недоступен. Формулы вводятся вручную.')
      : tFallback('dashboard.fieldsEmpty', 'Нет доступных полей');
    return <div className="text-muted small">{emptyMessage}</div>;
  }

  return (
    <div>
      <input
        className="form-control form-control-sm mb-2"
        type="text"
        placeholder={tFallback('dashboard.fieldsSearch', 'Поиск по полям...')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        data-testid="field-search-input"
      />
      {filteredFields.length === 0 ? (
        <div className="text-muted small">{tFallback('dashboard.fieldsNoResults', 'Ничего не найдено')}</div>
      ) : (
        filteredFields.map((field) => (
          <div className="field-item" key={field.fieldName} data-testid={`field-item-${field.fieldName}`}>
            <div className="field-item-label">{field.label}</div>
            <div className="field-item-description">{field.description}</div>
            <code className="field-item-name">this.{field.fieldName}</code>
          </div>
        ))
      )}
    </div>
  );
}
